import { Link } from 'react-router-dom';
import { useLocation } from '@/contexts/LocationContext';
import { GraduationCap, Target, BookOpen, Calendar, MessageSquare, Trophy, Clock, CheckCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import BlurImage from '@/components/ui/blur-image';
import { coursesForState, type AustralianState, type CourseDefinition } from '@/data/courses/courses';

const BabelStyleHero = () => {
  const {
    selectedState,
    currentStateConfig
  } = useLocation();
  
  const [isVisible, setIsVisible] = useState(false);
  const collageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (collageRef.current) {
      observer.observe(collageRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const courseIcons: Record<CourseDefinition['iconName'], React.ElementType> = {
    GraduationCap, Target, BookOpen, Zap: Target, Code: BookOpen, Trophy,
    Brain: Target, Clock, ClipboardCheck: CheckCircle,
  };
  const courses = coursesForState(selectedState.toUpperCase() as AustralianState)
    .filter((course) => course.homepageCategory)
    .map((course) => ({ title: course.name, href: course.href, icon: courseIcons[course.iconName] }))
    .concat({ title: 'Book an Assessment', href: '/book-assessment', icon: Calendar });

  // Different images for each tile
  const images = {
    main: '/lovable-uploads/babel-hero-tutoring-optimized.webp',
    mainMobile: '/lovable-uploads/babel-hero-tutoring-mobile.webp',
    topLeft: '/lovable-uploads/personalised-learning-showcase-optimized.webp',
    topLeftMobile: '/lovable-uploads/personalised-learning-showcase-mobile.webp',
    bottomLeft: '/lovable-uploads/foundations-hero-2025-optimized.webp',
    bottomLeftMobile: '/lovable-uploads/foundations-hero-2025-mobile.webp',
    middleSmall: '/lovable-uploads/personalised-learning-showcase-optimized.webp',
    middleSmallMobile: '/lovable-uploads/personalised-learning-showcase-mobile.webp',
    bottomRight: '/lovable-uploads/vic-hero-new-2025-optimized.webp',
    bottomRightMobile: '/lovable-uploads/vic-hero-new-2025-mobile.webp',
    tutoring: '/lovable-uploads/tutoring-session-male-optimized.webp',
    tutoringMobile: '/lovable-uploads/tutoring-session-male-mobile.webp'
  };

  return <section className="relative bg-[#f5f1eb] overflow-hidden" style={{ minHeight: '520px' }}>
      <div className="container mx-auto px-4 py-8 sm:py-10 lg:py-16">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-16 items-center">
          {/* Left Side - Text and Course Buttons */}
          <div className="space-y-6 lg:space-y-10 z-10">
            <div className="space-y-3">
              <h1 className="text-3xl sm:text-4xl lg:text-6xl font-heading font-bold text-gray-900 leading-tight">
                Which <span className="italic text-primary">course</span> would
                <br className="hidden sm:block" />
                <span className="sm:hidden"> </span>you like to <span className="italic text-primary">enrol</span> in?
              </h1>
            </div>

            {/* Course Buttons Grid */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3 max-w-md">
              {courses.map((course, index) => {
              const IconComponent = course.icon;
              const isBookAssessment = course.title === 'Book an Assessment';
              return <Link key={index} to={course.href} className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 transition-all duration-300 group active:scale-[0.98] ${
                isBookAssessment 
                  ? 'bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-white font-medium rounded-full shadow-lg hover:shadow-xl transform hover:scale-105' 
                  : 'bg-white border border-gray-200 hover:border-primary hover:shadow-md rounded-lg'
              }`}>
                    <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
                      {typeof IconComponent === 'function' && IconComponent.name ? <IconComponent className={`w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform ${isBookAssessment ? 'text-primary-foreground' : 'text-primary'}`} /> : <IconComponent />}
                    </span>
                    <span className={`text-xs sm:text-sm font-medium transition-colors line-clamp-1 ${isBookAssessment ? 'text-primary-foreground' : 'text-gray-800 group-hover:text-primary'}`}>
                      {course.title}
                    </span>
                  </Link>;
            })}
            </div>

            {/* State indicator */}
            <div className="space-y-2">
              <p className="text-xs sm:text-sm text-gray-500">
                Showing courses for {currentStateConfig?.name || 'NSW'}
              </p>
              <Link
                to="/naplan-feedback"
                aria-label="Share your NAPLAN parent feedback with Schoolola"
                className="inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-primary underline underline-offset-4 hover:opacity-80 transition-opacity"
              >
                <MessageSquare className="w-4 h-4" />
                NAPLAN parent feedback
              </Link>
            </div>
          </div>

          {/* Mobile/Tablet Image Collage - Asymmetric Layout */}
          <div ref={collageRef} className="relative lg:hidden order-last">
            <div className="relative h-[300px] sm:h-[380px]">
              {/* Main Large Center Image */}
              <div className={`absolute top-0 left-[15%] w-[55%] h-[65%] rounded-xl overflow-hidden shadow-xl z-20 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <BlurImage src={images.main} mobileSrc={images.mainMobile} alt="Students learning at Schoolola" className="w-full h-full" eager parallaxSpeed={0.3} />
              </div>
              
              {/* Top Left */}
              <div className={`absolute top-0 left-0 w-[22%] h-[30%] rounded-lg overflow-hidden shadow-lg transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}>
                <BlurImage src={images.topLeft} mobileSrc={images.topLeftMobile} alt="Students learning at Schoolola" className="w-full h-full" parallaxSpeed={0.5} />
              </div>
              
              {/* Yellow Card - Top Right */}
              <div className={`absolute top-0 right-0 w-[28%] h-[38%] rounded-xl bg-accent shadow-lg flex flex-col justify-end p-2 sm:p-3 text-white overflow-hidden transition-all duration-700 delay-150 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}`}>
                <span className="text-3xl sm:text-4xl font-bold opacity-30 absolute top-1 right-1">new</span>
                <span className="text-xl sm:text-2xl font-bold opacity-20 absolute top-8 right-2">new</span>
                <p className="text-xs sm:text-sm opacity-90 relative z-10">10+ courses</p>
                <div className="w-8 h-0.5 bg-primary mt-1 rounded-full"></div>
              </div>
              
              {/* Bottom Left - Excellence overlay */}
              <div className={`absolute bottom-[8%] left-0 w-[32%] h-[45%] rounded-xl overflow-hidden shadow-xl transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                <BlurImage src={images.bottomLeft} mobileSrc={images.bottomLeftMobile} alt="Foundation learning" className="w-full h-full" parallaxSpeed={0.4} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#8B4557]/90 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-2">
                  <p className="text-white font-semibold text-[10px] sm:text-xs">Excellence in Education</p>
                  <p className="text-white/70 text-[8px] sm:text-[10px]">By Schoolola</p>
                </div>
              </div>
              
              {/* Middle Small - Personalised learning */}
              <div className={`absolute bottom-0 left-[28%] w-[25%] h-[28%] rounded-lg overflow-hidden shadow-lg transition-all duration-700 delay-250 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}>
                <BlurImage src={images.middleSmall} mobileSrc={images.middleSmallMobile} alt="Personalised learning" className="w-full h-full" parallaxSpeed={0.2} />
              </div>
              
              {/* Tutoring Session - Center Bottom */}
              <div className={`absolute bottom-[5%] left-[48%] w-[32%] h-[35%] rounded-xl overflow-hidden shadow-xl z-10 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                <BlurImage src={images.tutoring} mobileSrc={images.tutoringMobile} alt="Tutoring session" className="w-full h-full" parallaxSpeed={0.35} />
              </div>
              
              {/* Bottom Right - Student success */}
              <div className={`absolute top-[42%] right-0 w-[26%] h-[32%] rounded-lg overflow-hidden shadow-lg transition-all duration-700 delay-350 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}`}>
                <BlurImage src={images.bottomRight} mobileSrc={images.bottomRightMobile} alt="Student success" className="w-full h-full" parallaxSpeed={0.45} />
              </div>
            </div>
          </div>

          {/* Right Side - Image Collage Grid (Desktop only) */}
          <div className="relative h-[450px] lg:h-[520px] hidden lg:block">
            {/* Grid Container */}
            <div className="absolute inset-0 grid grid-cols-12 grid-rows-12 gap-3">
              
              {/* Top Left Small Image */}
              <div className="col-start-1 col-span-3 row-start-1 row-span-4 rounded-xl overflow-hidden shadow-lg transform hover:scale-105 transition-transform duration-300">
                <BlurImage src={images.topLeft} alt="Students learning at Schoolola" className="w-full h-full" eager parallaxSpeed={0.4} />
              </div>

              {/* Main Large Center Image */}
              <div className="col-start-4 col-span-6 row-start-1 row-span-8 rounded-2xl overflow-hidden shadow-2xl z-20 transform hover:scale-[1.02] transition-transform duration-300">
                <BlurImage src={images.main} alt="Students learning at Schoolola" className="w-full h-full" eager parallaxSpeed={0.2} />
              </div>

              {/* Yellow "New" Card - Top Right */}
              <div className="col-start-10 col-span-3 row-start-1 row-span-5 rounded-xl bg-accent shadow-lg flex flex-col justify-end p-4 text-white overflow-hidden relative">
                <span className="text-5xl font-bold opacity-30 absolute top-2 right-2">new</span>
                <span className="text-4xl font-bold opacity-20 absolute top-10 right-4">new</span>
                <div className="relative z-10">
                  <p className="text-sm opacity-90">10+ courses</p>
                  <div className="w-12 h-1 bg-primary mt-2 rounded-full"></div>
                </div>
              </div>

              {/* Bottom Left Image with Overlay */}
              <div className="col-start-1 col-span-4 row-start-5 row-span-5 rounded-xl overflow-hidden shadow-xl relative">
                <BlurImage src={images.bottomLeft} alt="Foundation learning" className="w-full h-full" parallaxSpeed={0.35} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#8B4557]/90 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="text-white font-semibold text-sm">Excellence in Education</p>
                  <p className="text-white/70 text-xs">By Schoolola</p>
                </div>
              </div>

              {/* Middle Small Image */}
              <div className="col-start-3 col-span-3 row-start-10 row-span-3 rounded-lg overflow-hidden shadow-lg transform hover:scale-105 transition-transform duration-300">
                <BlurImage src={images.middleSmall} alt="Personalised learning" className="w-full h-full" parallaxSpeed={0.25} />
              </div>

              {/* Tutoring Session Image */}
              <div className="col-start-6 col-span-4 row-start-9 row-span-4 rounded-xl overflow-hidden shadow-xl z-10 transform hover:scale-105 transition-transform duration-300">
                <BlurImage src={images.tutoring} alt="Tutoring session" className="w-full h-full" parallaxSpeed={0.3} />
              </div>

              {/* Bottom Right Small Image */}
              <div className="col-start-10 col-span-3 row-start-6 row-span-4 rounded-lg overflow-hidden shadow-lg transform hover:scale-105 transition-transform duration-300">
                <BlurImage src={images.bottomRight} alt="Student success" className="w-full h-full" parallaxSpeed={0.5} />
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default BabelStyleHero;